import React, { useEffect, useState } from "react";
import { Button, Form, Spin, Modal, Popconfirm, Dropdown, Menu } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { PAGE_SIZE } from "../../constants";
import { Link } from "react-router-dom";
import { ReactSVG } from "react-svg";
import moment from "moment";
import {
	createNotification,
	deleteNotification,
	fetchNotifications,
	resetDeleteNotification,
	updateNofication,
	fetchStores,
} from "../../actions/notifications";
import NotificationsTable from "../Tables/Notifications";
import NotificationForm from "../Forms/Notification";
import "./Notifications.scss";
import { handleGetFileName } from "../../helpers/utils";

const NotificationsPage = () => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [file, setFile] = useState(null);
	const { notifications, loading } = useSelector(
		(state) => state.fetchNotifications,
	);

	const { stores, loading: storeLoading } = useSelector(
		(state) => state.fetchStores,
	);

	const { notificationDeleted } = useSelector(
		(state) => state.deleteNotification,
	);
	const { status: updateStatus } = useSelector(
		(state) => state.updateNotification,
	);
	const [currentPage, setCurrentPage] = useState(1);
	const [pagination, setPagination] = useState({});
	const [notificationId, setNotificationId] = useState(null);
	const limit = Number(PAGE_SIZE) || 20;
	const [screenState, setScreenState] = useState();
	const [type, setType] = useState("general");

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleOk = () => {
		setIsModalVisible(false);
		form.resetFields();
		setNotificationId(null);
		setFile(null);
		setScreenState(null);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		form.resetFields();
		setNotificationId(null);
		setFile(null);
		setScreenState(null);
	};

	const handleEdit = (record) => {
		const newFile = record.image ? handleGetFileName(record.image) : null;
		showModal();
		setNotificationId(record.id);
		if (
			[
				"category",
				"survey category",
				"products",
				"product",
				"review",
				"webview",
			].includes(record.path_to.screen)
		) {
			setScreenState(record.path_to.screen);
		} else {
			setScreenState();
		}
		setType(record?.type);
		form.setFieldsValue({
			...record,
			send_at: record.send_at
				? moment(record.send_at).utcOffset("+08:00")
				: null,
			start_time: record.start_time
				? moment(record.start_time).utcOffset("+08:00")
				: null,
			end_time: record.end_time
				? moment(record.end_time).utcOffset("+08:00")
				: null,
			file: { fileList: newFile },
			screen: record.path_to.screen,
			path_to_id: record.path_to.id,
			store_ids: record.store_ids,
		});
		record.image && setFile([newFile]);
	};

	const handleClickDelete = (record) => {
		dispatch(deleteNotification(record.id));
	};

	const addNewClick = () => {
		showModal();
		form.resetFields();
		setScreenState(null);
		setNotificationId(null);
		setFile(null);
	};

	const handleSubmitForm = (values) => {
		const newValues = { ...values };
		newValues.file = file && !file[0].url ? file[0] : null;
		if (values.send_at)
			newValues.send_at = moment(values.send_at).format("DD/MM/YYYY HH:mm");

		if (values.start_time && values.end_time) {
			newValues.start_time = moment(values.start_time).format(
				"DD/MM/YYYY HH:mm",
			);
			newValues.end_time = moment(values.end_time).format("DD/MM/YYYY HH:mm");
		}
		newValues.path_to = {
			screen: values.screen,
			id: values.path_to_id ? values.path_to_id : null,
		};
		delete newValues.screen;
		delete newValues.path_to_id;
		if (notificationId) {
			dispatch(updateNofication(notificationId, newValues));
		} else {
			dispatch(createNotification(newValues));
		}
		handleOk();
	};

	const handleFilter = (params) => {
		setCurrentPage(params.page);
		const startAt = (params.page - 1) * Number(PAGE_SIZE);
		dispatch(fetchNotifications({ start: Number(startAt), count: PAGE_SIZE }));
	};

	const handleTableChange = (params) => {
		handleFilter(params);
	};

	useEffect(() => {
		if (notificationDeleted) {
			dispatch(fetchNotifications({ start: 0, count: PAGE_SIZE }));
			dispatch(resetDeleteNotification());
		}
	}, [notificationDeleted]);

	useEffect(() => {
		if (updateStatus === "SUCCEEDED")
			dispatch(fetchNotifications({ start: 0, count: PAGE_SIZE }));
	}, [updateStatus]);

	useEffect(() => {
		if (notifications) {
			setPagination({
				showSizeChanger: false,
				current: currentPage,
				pageSize: limit,
				total: notifications.data.total,
				showTotal: (total) => `${total} Items`,
			});
		}
	}, [notifications]);

	useEffect(() => {
		dispatch(fetchNotifications({ start: 0, count: PAGE_SIZE }));
		dispatch(fetchStores({}));
	}, []);

	const withAction = (record) => {
		return (
			<span className="action" style={{ display: "flex" }}>
				<Dropdown.Button overlay={() => tableActionMenu(record)} />
			</span>
		);
	};

	const tableActionMenu = (record) => (
		<Menu className="table-action-menu">
			<Menu.Item key="view">
				<Link to={`/notifications/${record?.id}`}>
					<Button className="btn-table-action-primary" type="link">
						Show
					</Button>
				</Link>
			</Menu.Item>
			<Menu.Item key="edit">
				<Button
					className="btn-table-action-primary"
					type="link"
					onClick={() => handleEdit(record)}
				>
					Edit
				</Button>
			</Menu.Item>
			<Menu.Item key="delete">
				<Popconfirm
					title="Are you sure to delete this one?"
					onConfirm={() => handleClickDelete(record)}
					okText="Yes"
					cancelText="No"
				>
					<Button type="link" className="btn-table-action-danger">
						Delete
					</Button>
				</Popconfirm>
			</Menu.Item>
		</Menu>
	);

	return (
		<Spin spinning={loading} tip="Loading ...">
			<div className="notifications-container">
				<div className="page-header">
					<div className="page-header-breadbrumb">
						<ul>
							<li>Notifications</li>
						</ul>
					</div>
					<div>
						<Button
							type="primary"
							className="btn btn-primary"
							onClick={addNewClick}
						>
							New Notification
							<ReactSVG src="/icons/post-add.svg" />
						</Button>
					</div>
				</div>
				<div className="page-content">
					{notifications && (
						<NotificationsTable
							rowKey="id"
							dataSource={notifications.data.data}
							pagination={pagination}
							onChange2={handleTableChange}
							scrollHeight="calc(100vh - 275px)"
							actionWidth={50}
							withAction={withAction}
							stores={stores}
						/>
					)}
				</div>
				<Modal
					className="spin-form-modal"
					visible={isModalVisible}
					onOk={handleOk}
					onCancel={handleCancel}
					footer={null}
				>
					{notifications && (
						<NotificationForm
							form={form}
							notificationId={notificationId}
							handleOk={handleOk}
							handleSubmitForm={handleSubmitForm}
							handleCancel={handleCancel}
							setFile={setFile}
							file={file}
							screenState={screenState}
							setScreenState={setScreenState}
							stores={stores}
							storeLoading={storeLoading}
							setType={setType}
							type={type}
						/>
					)}
				</Modal>
			</div>
		</Spin>
	);
};

export default NotificationsPage;
